import { SKY_WORLD_ASSETS } from './86-sky-world-scale.mjs';
/** Local prototype rig data is injected separately from the licensed model. This
 * boundary accepts a short ancestor chain, not arbitrary scene or executable data. */
const checked = new WeakMap();
const vector = (value, size, limit = 100) =>
  Array.isArray(value) &&
  value.length === size &&
  value.every((v) => Number.isFinite(v) && Math.abs(v) <= limit);
const label = (value) => typeof value === 'string' && value.length > 0 && value.length <= 128;
const freeze = (value) => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};

/** Validate and copy metre-scale, Y-up glTF rest transforms. Returns null on
 * malformed input. At most 32 nodes and one socket keep per-tick work bounded;
 * parents must precede children, preventing cycles and ambiguous ancestry. */
export function validateSkyPrototypeRig(value) {
  if (!value || typeof value !== 'object') return null;
  if (checked.has(value)) return checked.get(value);
  const reject = () => {
    checked.set(value, null);
    return null;
  };
  if (
    value.version !== 1 ||
    !Number.isFinite(value.modelScale) ||
    value.modelScale <= 0 ||
    value.modelScale > 100 ||
    !label(value.clip) ||
    !Array.isArray(value.chain) ||
    value.chain.length < 1 ||
    value.chain.length > 32
  )
    return reject();
  const names = new Set(),
    chain = [];
  for (const [index, node] of value.chain.entries()) {
    if (
      !node ||
      !label(node.name) ||
      names.has(node.name) ||
      !Number.isInteger(node.parent) ||
      node.parent < -1 ||
      node.parent >= index ||
      !vector(node.position, 3) ||
      !vector(node.quaternion, 4, 1.001) ||
      Math.abs(Math.hypot(...node.quaternion) - 1) > 0.001 ||
      !vector(node.scale, 3) ||
      node.scale.some((v) => v <= 0)
    )
      return reject();
    names.add(node.name);
    const entry = {
      name: node.name,
      parent: node.parent,
      position: [...node.position],
      quaternion: [...node.quaternion],
      scale: [...node.scale],
    };
    if (node.control !== undefined) {
      const controls = [node.control];
      if (node.control?.secondary !== undefined) controls.push(node.control.secondary);
      const accepted = [];
      for (const c of controls) {
        if (
          !c ||
          !vector(c.axis, 3, 1.001) ||
          Math.abs(Math.hypot(...c.axis) - 1) > 0.001 ||
          !Number.isFinite(c.amplitude) ||
          Math.abs(c.amplitude) > 0.6 ||
          !Number.isFinite(c.frequency) ||
          c.frequency < 0 ||
          c.frequency > 8 ||
          !Number.isFinite(c.phase) ||
          Math.abs(c.phase) > Math.PI * 2 ||
          !Number.isFinite(c.opening) ||
          Math.abs(c.opening) > 1.2 ||
          (c.harmonic !== undefined && (!Number.isFinite(c.harmonic) || Math.abs(c.harmonic) > 0.1))
        )
          return reject();
        accepted.push({
          axis: [...c.axis],
          amplitude: c.amplitude,
          frequency: c.frequency,
          phase: c.phase,
          opening: c.opening,
          ...(c.harmonic !== undefined ? { harmonic: c.harmonic } : {}),
        });
      }
      entry.control = accepted[0];
      if (accepted[1]) entry.control.secondary = accepted[1];
    }
    chain.push(entry);
  }
  const socket = value.socket,
    body = value.body;
  if (
    !socket ||
    !Number.isInteger(socket.bone) ||
    socket.bone < 0 ||
    socket.bone >= chain.length ||
    !vector(socket.position, 3) ||
    !Number.isFinite(socket.radius) ||
    socket.radius < 0.5 ||
    socket.radius > 1.5 ||
    !Number.isFinite(socket.criticalRadius) ||
    socket.criticalRadius < 0.2 ||
    socket.criticalRadius > 0.45 ||
    socket.criticalRadius >= socket.radius ||
    !body ||
    !vector(body.center, 3) ||
    !Number.isFinite(body.radius) ||
    body.radius < 1 ||
    body.radius > 12
  )
    return reject();
  let gaze;
  if (value.gaze !== undefined) {
    const g = value.gaze;
    if (
      !g ||
      !Number.isInteger(g.bone) ||
      g.bone < 0 ||
      g.bone >= chain.length ||
      !vector(g.forward, 3, 1.001) ||
      !vector(g.up, 3, 1.001) ||
      Math.abs(Math.hypot(...g.forward) - 1) > 0.001 ||
      Math.abs(Math.hypot(...g.up) - 1) > 0.001 ||
      Math.abs(g.forward.reduce((sum, v, i) => sum + v * g.up[i], 0)) > 0.001 ||
      !Number.isFinite(g.maxYaw) ||
      g.maxYaw <= 0 ||
      g.maxYaw > 1.6 ||
      !Number.isFinite(g.maxPitch) ||
      g.maxPitch <= 0 ||
      g.maxPitch > 0.6
    )
      return reject();
    gaze = {
      bone: g.bone,
      forward: [...g.forward],
      up: [...g.up],
      maxYaw: g.maxYaw,
      maxPitch: g.maxPitch,
    };
  }
  const result = freeze({
    version: 1,
    modelScale: value.modelScale,
    clip: value.clip,
    chain,
    ...(gaze ? { gaze } : {}),
    socket: {
      bone: socket.bone,
      position: [...socket.position],
      radius: socket.radius,
      criticalRadius: socket.criticalRadius,
    },
    body: { center: [...body.center], radius: body.radius },
  });
  checked.set(value, result);
  checked.set(result, result);
  return result;
}

const multiply = ([ax, ay, az, aw], [bx, by, bz, bw]) => [
  ax * bw + aw * bx + ay * bz - az * by,
  ay * bw + aw * by + az * bx - ax * bz,
  az * bw + aw * bz + ax * by - ay * bx,
  aw * bw - ax * bx - ay * by - az * bz,
];
const rotate = ([x, y, z], [qx, qy, qz, qw]) => {
  const tx = 2 * (qy * z - qz * y),
    ty = 2 * (qz * x - qx * z),
    tz = 2 * (qx * y - qy * x);
  return [
    x + qw * tx + qy * tz - qz * ty,
    y + qw * ty + qz * tx - qx * tz,
    z + qw * tz + qx * ty - qy * tx,
  ];
};

/** Shared respiratory envelope. Inhale follows the authoritative charge, then
 * exhale drains the stored expansion over the actual active jet duration.
 * Traversal and defeat use the neutral chest; paused frames reuse the same data. */
export function skyDragonBreathing(item = {}) {
  const smooth = (value) => {
    const t = Math.max(0, Math.min(1, value));
    return t * t * (3 - 2 * t);
  };
  if (item.traversing || item.defeated) return { inflation: 0, nostrilFlare: 0, phase: 'rest' };
  const active =
    item.breath?.active && Number.isFinite(item.breath.duration) && item.breath.duration > 0;
  const progress = active
    ? Math.max(0, Math.min(1, (item.breath.age ?? 0) / item.breath.duration))
    : 0;
  const charge = Number.isFinite(item.charge) ? item.charge : 0;
  const inflation = active ? 1 - smooth(progress) : smooth(charge);
  return {
    inflation,
    nostrilFlare: active ? Math.max(inflation, Math.sin(Math.PI * progress) * 0.75) : inflation,
    phase: active ? 'exhale' : inflation > 0 ? 'inhale' : 'rest',
  };
}

/** Sample the exact local bone pose used by both renderer and collision. Time is
 * simulation seconds; opening is the canonical 0..1 aperture blend. Secondary
 * controls compose after primary yaw, allowing local pitch without a second clock. Invalid rig
 * returns null, while invalid clocks resolve to a closed, neutral-time pose. */
export function sampleSkyPrototypeRig(
  value,
  activeTime,
  apertureOpening,
  gazeTarget,
  facingFocus = apertureOpening,
  chestInflation = 0,
) {
  const rig = validateSkyPrototypeRig(value);
  if (!rig) return null;
  const time = Number.isFinite(activeTime) ? Math.max(0, activeTime) : 0;
  const opening = Number.isFinite(apertureOpening) ? Math.max(0, Math.min(1, apertureOpening)) : 0;
  const focus = Math.max(0, Math.min(1, Number.isFinite(facingFocus) ? facingFocus : opening));
  const inflation = Number.isFinite(chestInflation) ? Math.max(0, Math.min(1, chestInflation)) : 0;
  // Local Y is the spine's longitudinal axis. Two chest joints share a16%
  // cross-section increase, while their combined longitudinal change is2.5%.
  const transverse = Math.sqrt(1 + inflation * 0.16);
  const longitudinal = Math.sqrt(1 + inflation * 0.025);
  const joints = rig.chain.map((node) => {
    let quaternion = node.quaternion;
    if (node.control) {
      for (const c of [node.control, node.control.secondary].filter(Boolean)) {
        const phase = time * c.frequency + c.phase;
        const harmonic = c.harmonic ?? 0;
        // A small phase-locked overtone varies bend speed without random jitter
        // or exceeding the authored joint amplitude.
        const wave =
          (Math.sin(phase) + harmonic * Math.sin(phase * 2 + 0.4)) / (1 + Math.abs(harmonic));
        // Five small pitch contributions lower the enlarged neck toward its
        // opponent without translating the head or detaching collision sockets.
        const bow =
          node.name.startsWith('Azure_Neck_') && c === node.control.secondary ? focus * 0.1 : 0;
        const angle = wave * c.amplitude * (1 - focus * 0.6) + opening * c.opening + bow;
        quaternion = multiply(quaternion, [
          ...c.axis.map((v) => v * Math.sin(angle / 2)),
          Math.cos(angle / 2),
        ]);
      }
    }
    const chest = ['tripo::Spine_1', 'tripo::Spine_2'].includes(node.name);
    const scale = chest
      ? node.scale.map((v, axis) => v * (axis === 1 ? longitudinal : transverse))
      : node.scale;
    return { ...node, quaternion, scale };
  });
  if (rig.gaze && vector(gazeTarget, 3, 1000)) applyGaze(rig, joints, gazeTarget);
  let position = [...rig.socket.position],
    index = rig.socket.bone;
  while (index >= 0) {
    const node = joints[index];
    position = rotate(
      position.map((v, i) => v * node.scale[i]),
      node.quaternion,
    ).map((v, i) => v + node.position[i]);
    index = node.parent;
  }
  return {
    rig,
    joints,
    chestExpansion:
      transverse **
      rig.chain.filter((node) => ['tripo::Spine_1', 'tripo::Spine_2'].includes(node.name)).length,
    position: position.map((v) => v * rig.modelScale * SKY_WORLD_ASSETS.dragon.modelScale),
  };
}

const conjugate = ([x, y, z, w]) => [-x, -y, -z, w];
const axisRotation = (axis, angle) => [
  ...axis.map((v) => v * Math.sin(angle / 2)),
  Math.cos(angle / 2),
];
// Resolve the target through the already sampled moving ancestors. Gaze replaces
// free head yaw, counteracting body bend instead of adding another oscillation.
function applyGaze(rig, joints, target) {
  const g = rig.gaze,
    path = [];
  for (let index = g.bone; index >= 0; index = joints[index].parent) path.unshift(index);
  let local = target.map((v) => v / (rig.modelScale * SKY_WORLD_ASSETS.dragon.modelScale));
  for (const index of path) {
    const node = joints[index];
    const quaternion = index === g.bone ? rig.chain[index].quaternion : node.quaternion;
    local = rotate(
      local.map((v, i) => v - node.position[i]),
      conjugate(quaternion),
    ).map((v, i) => v / node.scale[i]);
  }
  const [ux, uy, uz] = g.up,
    [fx, fy, fz] = g.forward;
  const right = [uy * fz - uz * fy, uz * fx - ux * fz, ux * fy - uy * fx];
  const dot = (axis) => axis.reduce((sum, v, i) => sum + v * local[i], 0);
  const x = dot(right),
    y = dot(g.up),
    z = dot(g.forward);
  if (Math.hypot(x, y, z) < 1e-6) return;
  const yaw = Math.max(-g.maxYaw, Math.min(g.maxYaw, Math.atan2(x, z)));
  const pitch = Math.max(-g.maxPitch, Math.min(g.maxPitch, Math.atan2(y, Math.hypot(x, z))));
  joints[g.bone].quaternion = multiply(
    rig.chain[g.bone].quaternion,
    multiply(axisRotation(g.up, yaw), axisRotation(right, -pitch)),
  );
}

/** Metre-space gaze destination: the protected close approach addresses the
 * viewer with a 2.4m midpoint between portrait/landscape shoulder cameras, then
 * smoothly returns to the actual rider before combat starts. The
 * caller supplies canonical bounded player coordinates; invalid values fall back
 * to centre flight. This target never authorizes damage or changes the camera. */
export function skyPrototypeGazeTarget(player, age, travel) {
  const x = Number.isFinite(player?.x) ? player.x : 0;
  const y = Number.isFinite(player?.y) ? player.y : 5.2;
  const t = Math.max(0, Math.min(1, ((Number.isFinite(age) ? age : 0) - 3.1) / 1.4));
  const blend = t * t * (3 - 2 * t);
  const anticipation = Math.max(0, Math.min(1, ((Number.isFinite(age) ? age : 0) - 4.5) / 1.8));
  const focus = Math.max(
    travel?.opening ?? 0,
    travel?.charge ?? 0,
    travel?.vulnerable || ['charge', 'strike'].includes(travel?.attackPhase) ? 1 : 0,
  );
  const weight =
    anticipation * anticipation * (3 - 2 * anticipation) * (1 - Math.max(0, Math.min(1, focus)));
  const vx = Number.isFinite(travel?.vx) ? travel.vx : 0;
  const vy = Number.isFinite(travel?.vy) ? travel.vy : 0;
  return [
    x + 2.4 * (1 - blend) + weight * Math.max(-5, Math.min(5, vx * 1.1)),
    6.2 + (y - 6.2) * blend + weight * Math.max(-1.5, Math.min(1.5, vy * 0.8)),
    7.75 - 6.75 * blend,
  ];
}

/** Measured original nostril centres and outward vectors in Head_2 local space.
 * These are anatomical anchors on the accepted source mesh, shared by the
 * sampled skeleton, local nostril deformation and warm exhalation artwork. */
export const SKY_DRAGON_NOSTRILS = Object.freeze([
  Object.freeze({
    position: Object.freeze([-0.0502644312203335, 0.07241286918207146, -0.0061025368002379475]),
    direction: Object.freeze([-0.5878291153983288, 0.48487297252501815, -0.6475763519499236]),
  }),
  Object.freeze({
    position: Object.freeze([-0.05015413076105253, 0.07240436670272637, 0.0024881546218935803]),
    direction: Object.freeze([-0.5878296983023809, 0.48487312754179884, 0.647575706756798]),
  }),
]);
function headPoint(sampled, point) {
  let index = sampled?.joints.findIndex((node) => node.name === 'tripo::Head_2') ?? -1;
  if (index < 0) return null;
  let position = [...point];
  while (index >= 0) {
    const node = sampled.joints[index];
    position = rotate(
      position.map((v, i) => v * node.scale[i]),
      node.quaternion,
    ).map((v, i) => v + node.position[i]);
    index = node.parent;
  }
  return position.map((v) => v * sampled.rig.modelScale * SKY_WORLD_ASSETS.dragon.modelScale);
}
/** Measured lip-opening anchor on the shared scaled Head_2 chain. Alternate
 * test rigs without this anatomical node return null rather than a fake mouth. */
export function skyPrototypeMouth(sampled) {
  return headPoint(sampled, [-0.06273452246586017, 0.0627012878416976, -0.0000022519538739509525]);
}

/** Centre of the actual weighted head shell in Head_2 local metres. Cameras
 * may frame this anatomical point without moving gameplay or targeting the lip. */
export function skyPrototypeHeadFocus(sampled) {
  return headPoint(sampled, [0.007463999446741643, 0.040808620610707114, -0.0019618976376907793]);
}
/** Resolve both nostrils and their unit exhalation vectors in model metres.
 * Missing Head_2 returns an empty list; no camera-dependent offset is applied. */
export function skyPrototypeNostrils(sampled) {
  const result = [];
  for (const anchor of SKY_DRAGON_NOSTRILS) {
    const position = headPoint(sampled, anchor.position);
    if (!position) return [];
    const tip = headPoint(
      sampled,
      anchor.position.map((v, i) => v + anchor.direction[i]),
    );
    const vector = tip.map((v, i) => v - position[i]),
      length = Math.hypot(...vector);
    result.push({ position, direction: vector.map((v) => v / length) });
  }
  return result;
}
